import type { CreatedProduct } from '@/types/products'

// ─── Change product code (z8nrz7e72t) ────────────────────────────────────────
//
// Changing a product's code creates a NEW product under the new code and marks
// the old one superseded (`supersededById`). The old row is history: kept,
// shown greyed with what replaced it, and offered to no action — the server
// refuses split, pool, ship, label and storage on it too. Pure so it is tested;
// vitest only runs `src/**/*.test.ts`.

export function isSuperseded(p: Pick<CreatedProduct, 'supersededById'>): boolean {
  return !!p.supersededById
}

/** The products an action may be offered on — everything not superseded. */
export function actionableProducts<T extends Pick<CreatedProduct, 'supersededById'>>(products: readonly T[]): T[] {
  return products.filter(p => !isSuperseded(p))
}

/**
 * Both directions of every code change among `products`: what each superseded
 * product was replaced by, and what each replacement was changed from. Only
 * pairs where both rows are in the list — a list scoped to one form may hold
 * one side only, which then simply shows no link.
 */
export function supersessionLinks(products: readonly CreatedProduct[]): {
  replacedBy: Map<string, CreatedProduct>
  changedFrom: Map<string, CreatedProduct>
} {
  const byId = new Map(products.map(p => [p.id, p]))
  const replacedBy = new Map<string, CreatedProduct>()
  const changedFrom = new Map<string, CreatedProduct>()
  for (const old of products) {
    const replacement = old.supersededById ? byId.get(old.supersededById) : undefined
    if (!replacement) continue
    replacedBy.set(old.id, replacement)
    changedFrom.set(replacement.id, old)
  }
  return { replacedBy, changedFrom }
}

// ─── Used up by a split (z8nrz7g2wm) ─────────────────────────────────────────
//
// A split divides the whole bag: the original keeps its row as history, at
// 0 mL with no storage location, and its aliquots carry on. Like a superseded
// product it is shown greyed and offered no physical action (store, move,
// ship, split, pool, a new label). A split made before the server zeroed the
// volume still left aliquots behind, so having one counts as well.

export function isUsedUp(
  p: Pick<CreatedProduct, 'id' | 'volumeMl'>,
  products: readonly Pick<CreatedProduct, 'parentProductId'>[],
): boolean {
  return p.volumeMl === 0 || products.some(c => c.parentProductId === p.id)
}

/** The products a physical action may be offered on: not superseded, not used up. */
export function inHandProducts<
  T extends Pick<CreatedProduct, 'id' | 'supersededById' | 'volumeMl' | 'parentProductId'>,
>(products: readonly T[]): T[] {
  return actionableProducts(products).filter(p => !isUsedUp(p, products))
}
